import * as React from 'react'
import {
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import type { Captain } from 'types/fpl/elements'
import type { Chip } from 'types/fpl/chips'
import type { InterestScore, LeagueTemplate, TemplatinessStats } from 'api/league'
import type { LeagueTransferStats } from 'types/fpl/transfers'
import { formatDecimal } from '../../utils/numbers'
import { captainPct, resolveLeagueTeamCount } from '../../utils/leagueTeamCount'
import {
  createLeagueInsightsImage,
  saveImage,
  type ExportImage,
} from '../../utils/exportImage'
import { shirtImageSrc } from 'utils/shirtSrc'
import { colors } from 'constants/colors'
import CaptainDetailLine from 'components/structure/CaptainDetailLine'

interface Props {
  captains: Captain[]
  loading?: boolean
  totalTeams: number
  leagueName?: string
  gameweek?: string
  chips?: Chip[]
  interest?: InterestScore | null
  leagueTemplate?: LeagueTemplate | null
  templatiness?: TemplatinessStats | null
  transferStats?: LeagueTransferStats | null
  onExport?: () => void
  showHeaderButton?: boolean
  captainMultiplierTotal?: number
  /** element id → FPL pick multiplier (1 squad, 2 captain, 3 triple captain) */
  userPickMultipliers?: Record<number, number>
  /** When true, importance compares to league average excluding viewer (n−1 denominator) */
  viewerInLeague?: boolean
}

const sortCaptains = (list: Captain[]): Captain[] =>
  [...list].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count
    if (b.triple !== a.triple) return b.triple - a.triple
    const ta = a.player.team ?? 0
    const tb = b.player.team ?? 0
    if (ta !== tb) return ta - tb
    return (a.player.web_name ?? '').localeCompare(b.player.web_name ?? '')
  })

const CaptainsGrid: React.FC<Props> = ({
  captains,
  loading,
  totalTeams,
  leagueName,
  gameweek,
  chips,
  interest,
  leagueTemplate,
  templatiness,
  transferStats,
  onExport,
  showHeaderButton = false,
  captainMultiplierTotal = 0,
  userPickMultipliers = {},
  viewerInLeague = false,
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [exporting, setExporting] = React.useState(false)
  const [preparedImage, setPreparedImage] = React.useState<ExportImage | null>(null)
  const [expandedId, setExpandedId] = React.useState<number | null>(null)
  const isMobileDevice = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent)

  const sorted = React.useMemo(() => sortCaptains(captains), [captains])
  const leagueSize = React.useMemo(
    () => resolveLeagueTeamCount(totalTeams, captains),
    [totalTeams, captains],
  )
  const gwLabel = gameweek ?? '—'

  const handleExport = async () => {
    if (!leagueName || !gameweek || !chips || captains.length === 0) {
      return
    }

    setExporting(true)
    try {
      const image = await createLeagueInsightsImage({
        leagueName,
        gameweek,
        captains,
        chips: chips ?? [],
        totalTeams: leagueSize,
        isDarkMode: isDark,
        interest,
        leagueTemplate,
        templatiness,
        transferStats,
      })

      // Safari requires a fresh user gesture for navigator.share() after a
      // lengthy canvas conversion. Preparing first makes the next tap reliable.
      if (isMobileDevice) {
        setPreparedImage(image)
      } else {
        await saveImage(image)
        onExport?.()
      }
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setExporting(false)
    }
  }

  const handleSavePreparedImage = async () => {
    if (!preparedImage) return

    setExporting(true)
    try {
      await saveImage(preparedImage, true)
      setPreparedImage(null)
      onExport?.()
    } catch (error) {
      console.error('Image save failed:', error)
    } finally {
      setExporting(false)
    }
  }

  const exportDialog = (
    <Dialog open={Boolean(preparedImage)} onClose={() => !exporting && setPreparedImage(null)}>
      <DialogTitle>Image ready</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Tap Save image, then choose “Save Image” in your phone’s share menu.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreparedImage(null)} disabled={exporting}>Cancel</Button>
        <Button onClick={handleSavePreparedImage} disabled={exporting} variant="contained">
          Save image
        </Button>
      </DialogActions>
    </Dialog>
  )

  const shirtColWidth = isMobile ? 36 : 48
  const canExport = leagueName && gameweek && captains.length > 0

  if (showHeaderButton && canExport) {
    return (
      <>
        <Tooltip title="Download as Image">
          <IconButton
            onClick={handleExport}
            disabled={exporting || loading}
            size="small"
            sx={{
              color: isDark ? colors.WHITE : colors.ALMOST_BLACK,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        {exportDialog}
      </>
    )
  }

  const cellSx = {
    fontFamily: 'PremierLeagueRegular',
    fontSize: isMobile ? '0.75rem' : '0.875rem',
    py: isMobile ? 0.5 : 1,
    borderColor: 'divider',
  }

  const headSx = {
    ...cellSx,
    fontFamily: 'PremierLeagueBold',
    fontWeight: 600,
    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
  }

  const multTotal = React.useMemo(() => {
    if (captainMultiplierTotal && captainMultiplierTotal > 0) {
      return captainMultiplierTotal
    }
    const fromRows = captains.reduce((a, c) => a + (c.multiplier_sum ?? 0), 0)
    if (fromRows > 0) return fromRows
    return captains.reduce((a, c) => a + c.count, 0) * 2
  }, [captainMultiplierTotal, captains])

  return (
    <Box
      sx={{
        width: '100%',
        pb: 2.5,
        overflowX: 'auto',
        touchAction: 'pan-x pan-y',
      }}
    >
      <TableContainer
        sx={{
          minWidth: isMobile ? 320 : 480,
          ...(isMobile && {
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }),
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...headSx,
                  width: shirtColWidth,
                  minWidth: shirtColWidth,
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  backgroundColor: isDark ? theme.palette.background.paper : theme.palette.background.paper,
                }}
              />
              <TableCell
                sx={{
                  ...headSx,
                  minWidth: isMobile ? 100 : 120,
                  position: 'sticky',
                  left: shirtColWidth,
                  zIndex: 3,
                  boxShadow: isDark ? '4px 0 8px rgba(0,0,0,0.35)' : '4px 0 8px rgba(0,0,0,0.06)',
                  backgroundColor: isDark ? theme.palette.background.paper : theme.palette.background.paper,
                }}
              >
                Player
              </TableCell>
              <TableCell sx={{ ...headSx, textAlign: 'center', whiteSpace: 'nowrap' }}>Captained</TableCell>
              <TableCell sx={{ ...headSx, textAlign: 'center', whiteSpace: 'nowrap' }}>Captained %</TableCell>
              <TableCell sx={{ ...headSx, textAlign: 'center', whiteSpace: 'nowrap' }}>Triple</TableCell>
              <TableCell sx={{ ...headSx, width: 44, minWidth: 44, px: 0.5 }} aria-label="" />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((c) => {
              const id = c.player.id
              const expanded = expandedId === id
              const leaguePctNum = captainPct(c.count, totalTeams, captains)
              const leaguePct = formatDecimal(leaguePctNum)
              const captainedFraction = leagueSize ? `${c.count}/${leagueSize}` : '—'
              const startsCount = typeof c.starts_league_count === 'number' ? c.starts_league_count : null
              const startsPctBackend =
                typeof c.starts_league_pct === 'number' ? c.starts_league_pct : null
              const startsPctNum =
                startsPctBackend !== null
                  ? startsPctBackend
                  : startsCount !== null && totalTeams > 0
                    ? (startsCount / totalTeams) * 100
                    : 0
              const startsPct = formatDecimal(startsPctNum)
              const startsLeagueValue =
                startsCount !== null && totalTeams > 0
                  ? `${startsPct}% (${startsCount}/${totalTeams})`
                  : startsPctBackend !== null
                    ? `${startsPct}%`
                    : '—'
              const multSum = c.multiplier_sum ?? c.count * 2
              const startsWeightSum = c.starts_league_weight_sum
              const useStartsEffective = totalTeams > 0 && typeof startsWeightSum === 'number'
              const startsEffectiveBackend =
                typeof c.starts_league_effective_pct === 'number' ? c.starts_league_effective_pct : null
              const startsEffectiveNum =
                startsEffectiveBackend !== null
                  ? startsEffectiveBackend
                  : useStartsEffective
                    ? (startsWeightSum / totalTeams) * 100
                    : multTotal > 0
                      ? (multSum / multTotal) * 100
                      : 0
              const startsEffectivePct = formatDecimal(startsEffectiveNum)
              const myMult = id > 0 ? userPickMultipliers[id] ?? 0 : 0
              const userStakePct = myMult > 0 ? 100 * myMult : 0
              const peerBackend =
                typeof c.importance_peer_effective_pct === 'number' ? c.importance_peer_effective_pct : null
              let peerLeagueAvg = peerBackend !== null ? peerBackend : startsEffectiveNum
              if (peerBackend === null && useStartsEffective) {
                const sw = startsWeightSum as number
                if (viewerInLeague && totalTeams > 1) {
                  if (myMult > 0) {
                    peerLeagueAvg = (Math.max(0, sw - myMult) / (totalTeams - 1)) * 100
                  } else {
                    peerLeagueAvg = (sw / totalTeams) * 100
                  }
                } else if (viewerInLeague && totalTeams === 1) {
                  peerLeagueAvg = 0
                } else {
                  peerLeagueAvg = (sw / totalTeams) * 100
                }
              }
              const importance =
                typeof c.importance_pct === 'number'
                  ? formatDecimal(c.importance_pct)
                  : formatDecimal(userStakePct - peerLeagueAvg)
              const price = formatDecimal((c.player?.now_cost ?? 0) / 10)
              const tsbGlobal = parseFloat(c.player.selected_by_percent ?? '0')
              const gwPts = c.player.event_points ?? 0
              const shirtId = c.player.team ?? 0
              const scale = shirtId === 0 ? 1.6 : 1
              const imgW = Math.round((isMobile ? 16 : 22) * scale)
              const imgH = Math.round((isMobile ? 20 : 28) * scale)

              return (
                <React.Fragment key={id}>
                  <TableRow hover selected={expanded}>
                    <TableCell
                      sx={{
                        ...cellSx,
                        width: shirtColWidth,
                        minWidth: shirtColWidth,
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      <img
                        src={shirtImageSrc(shirtId)}
                        alt=""
                        style={{ width: imgW, height: imgH, objectFit: 'contain', display: 'block' }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellSx,
                        position: 'sticky',
                        left: shirtColWidth,
                        zIndex: 2,
                        boxShadow: isDark ? '4px 0 8px rgba(0,0,0,0.35)' : '4px 0 8px rgba(0,0,0,0.06)',
                        backgroundColor: theme.palette.background.paper,
                        fontFamily: 'PremierLeagueBold',
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      }}
                    >
                      {c.player.web_name}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellSx,
                        textAlign: 'center',
                        fontFamily: 'PremierLeagueBold',
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      }}
                    >
                      {c.count}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {leaguePct}%
                    </TableCell>
                    <TableCell sx={{ ...cellSx, textAlign: 'center' }}>{c.triple}</TableCell>
                    <TableCell sx={{ ...cellSx, width: 44, minWidth: 44, px: 0.5, textAlign: 'center', verticalAlign: 'middle' }}>
                      <IconButton
                        aria-label={expanded ? 'Collapse' : 'Expand'}
                        size="small"
                        onClick={() => setExpandedId(expanded ? null : id)}
                        disabled={id < 0}
                        sx={{ p: 0.25 }}
                      >
                        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0, border: 0, p: 0 }} colSpan={6}>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            px: { xs: 1, sm: 2 },
                            py: 1.5,
                            mx: 0,
                            mb: 0.5,
                            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                            borderTop: '1px solid',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1fr 1fr',
                                md: 'repeat(3, minmax(0, 1fr))',
                              },
                              gap: { xs: 1, sm: 1.25 },
                            }}
                          >
                            <CaptainDetailLine label="Current Price" value={`£${price}m`} />
                            <CaptainDetailLine
                              label="Starts in league"
                              value={startsLeagueValue}
                            />
                            <CaptainDetailLine label={`GW ${gwLabel} pts`} value={String(gwPts)} />
                            <CaptainDetailLine label="Total selected by" value={`${tsbGlobal.toFixed(1)}%`} />
                            <CaptainDetailLine
                              label="Starts in league effective"
                              value={`${startsEffectivePct}%`}
                            />
                            <CaptainDetailLine label={`Importance for you`} value={`${importance}%`} />
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {exportDialog}
    </Box>
  )
}

export default CaptainsGrid
